import { useState } from 'react';
import CertCallout from './CertCallout';
import { aboutCredentials, monthlyPlans, projectPlans, startLinkProps } from '../data';

function CheckList({ items }) {
  return (
    <ul className="check-list">
      {items.map((item) => (
        <li key={item}>
          <span className="check" aria-hidden="true">
            ✓
          </span>
          {item}
        </li>
      ))}
    </ul>
  );
}

const planOptions = Object.values(monthlyPlans);

export default function Pricing() {
  const [activePlan, setActivePlan] = useState('essential');
  const plan = monthlyPlans[activePlan];
  const activeIndex = planOptions.findIndex((option) => option.id === activePlan);

  return (
    <section className="section pricing" id="pricing">
      <div className="container">
        <div className="section-head has-cert" data-reveal>
          <div className="section-head-copy">
            <h2 className="display-2">
              Clear pricing. <em>Simple choices.</em>
            </h2>
            <p className="section-lede">
              Straightforward bookkeeping gets one set price. For more complex books we’ll provide a
              quote before work begins, so that there are no surprises.
            </p>
          </div>
          <CertCallout
            label="Intuit-trained. QuickBooks-certified."
            caption={
              <>
                Intuit-trained.
                <br />
                QuickBooks-certified.
              </>
            }
            marks={aboutCredentials}
          />
        </div>

        <div className="pricing-grid">
          {projectPlans.map((project) => (
            <article className="price-card" key={project.id} data-reveal>
              <div className="price-card-top" aria-hidden="true" />
              <p className="plan-name">{project.name}</p>
              <p className="plan-price">
                <span className={project.priceLead ? 'price-lead' : 'price-lead is-placeholder'}>
                  {project.priceLead || 'Starting at'}
                </span>
                <span className="price-figure">{project.price}</span>
                <span className={`price-unit${project.flushUnit ? ' is-flush' : ''}`}>
                  {project.unit}
                </span>
              </p>
              <p className="plan-copy">{project.copy}</p>
              <a className="btn btn-soft btn-block" {...startLinkProps}>
                Schedule a call
              </a>
              <div className="price-card-footer">
                <p className="includes-label">Included</p>
                <CheckList items={project.items} />
                {project.note && <p className="fine-print">{project.note}</p>}
              </div>
            </article>
          ))}

          <article className="price-card is-featured" data-reveal>
            <div className="price-card-top">
              <p className="eyebrow price-card-eyebrow">Monthly Bookkeeping</p>
              <div
                className={`plan-toggle${activePlan === 'custom' ? ' is-custom' : ''}`}
                role="group"
                aria-label="Choose a monthly bookkeeping plan"
              >
                <span
                  className="plan-toggle-thumb"
                  aria-hidden="true"
                  style={{ transform: `translateX(${activeIndex * 100}%)` }}
                />
                {planOptions.map((option) => (
                  <button
                    type="button"
                    key={option.id}
                    className={activePlan === option.id ? 'is-active' : ''}
                    aria-pressed={activePlan === option.id}
                    onClick={() => setActivePlan(option.id)}
                  >
                    {option.name}
                  </button>
                ))}
              </div>
            </div>

            <p className="plan-name">{plan.name}</p>
            <p className="plan-price">
              <span className="price-lead is-placeholder">Starting at</span>
              <span className={`price-figure${plan.priceText ? ' is-text' : ''}`}>{plan.price}</span>
              {plan.unit ? (
                <span className={`price-unit${plan.unit.startsWith('/') ? ' is-flush' : ''}`}>
                  {plan.unit}
                </span>
              ) : null}
            </p>
            <p className="plan-copy">
              {plan.copy}
              {plan.detail ? ` ${plan.detail}` : ''}
            </p>
            <a className="btn btn-primary btn-block" {...startLinkProps}>
              Schedule a call
            </a>
            <div className="price-card-footer">
              <p className="includes-label">Included</p>
              <CheckList items={plan.items} />
              {plan.note && <p className="fine-print">{plan.note}</p>}
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
